<template lang="pug">
ul
  li(
    v-for="(item, index) in items"
    :key="`item-row-${index}`"
    :class="{[$style.item]: true, 'zero': !getItemQuantity(item.fake_id)}"
    @click="!item.fake_id ? $emitter.emit('open-create-item', {'source': category}) : !getItemQuantity(item.fake_id) ? increaseQuantity(item, index) : null")
    .h-full.flex.justify-between.gap-ds-sm
      .h-full.flex.flex-col.justify-center.min-h-ds-xl
        div(v-html="$highlightMatch(item.name, search)")
        small(v-if="item.approx") {{ item.approx }}
      div(:class="$style.itemControls")
        template(v-if="getItemQuantity(item.fake_id)")
          .flex.items-center(@click.stop="decreaseQuantity(item, index)")
            Icon.text-blue-primary.cursor-pointer(
              name="i:subtract-circle-1-1"
              :size="24")
          p
            span {{ getItemQuantity(item.fake_id) }}
          .flex.items-center(@click.stop="increaseQuantity(item, index)")
            Icon.text-blue-primary.cursor-pointer(
              name="i:add-circle-1-1"
              :size="24")
        button(v-else) {{ $t('items.add') }}
</template>

<script lang="ts">
import { defineComponent } from "vue"
import type { PropType } from "vue"
import { mapGetters } from "vuex"
import { ADD_ITEM, DECREASE_ITEM, INCREASE_ITEM } from "~/store/listing"
import { GET_ITEMS_BY_FAKE_ID } from "~/store/listing"
import { FURNITURE_DEFAULT_INVENTORY } from "~/constants/item"

export default defineComponent({
  name: "ItemRows",
  props: {
    items: {
      type: Array as PropType<Item[]>,
      required: true
    },
    source: {
      type: String,
      required: true
    },
    category: {
      type: String,
      default: ""
    },
    search: {
      type: String,
      default: ""
    }
  },

  computed: {
    ...mapGetters("listing", {
      rows_getItemsByFakeId: GET_ITEMS_BY_FAKE_ID
    })
  },

  methods: {
    getItemQuantity(fakeId: string): number {
      return this.rows_getItemsByFakeId[fakeId]?.quantity
    },
    async increaseQuantity(storeItem, rank) {
      const item: Item = this.getItemClone(storeItem)
      if (item.quantity >= 100) return
      let addedItem: null | Item = null
      if (!item.quantity) {
        addedItem = await this.$store.dispatch("listing/" + ADD_ITEM, item)
        item.fake_id = addedItem?.fake_id
      } else {
        await this.$store.dispatch("listing/" + INCREASE_ITEM, item)
      }

      const itemID = item.item_id || item.name
      const quantity = item.quantity ? item.quantity + 1 : 1
      if (this.source === "category_item") {
        const categoryTitle = item.type ? FURNITURE_DEFAULT_INVENTORY[item.type].title : ""
        this.$snowplow.trackAddCategoryItem(itemID, quantity, categoryTitle)
      } else if (this.source === "popular_item") {
        this.$snowplow.trackAddPopularItem(itemID, quantity, rank)
      } else if (this.source === "search_item") {
        this.$snowplow.trackAddSearchItem(itemID, quantity, rank)
      }
    },

    async decreaseQuantity(storeItem, rank) {
      const item: Item = this.getItemClone(storeItem)
      if (item.quantity <= 0) return
      await this.$store.dispatch("listing/" + DECREASE_ITEM, item)

      const itemID = item.item_id || item.name
      const quantity = item.quantity ? item.quantity - 1 : 0
      if (this.source === "category_item") {
        const categoryTitle = item.type ? FURNITURE_DEFAULT_INVENTORY[item.type].title : ""
        this.$snowplow.trackRemoveCategoryItem(itemID, quantity, categoryTitle)
      } else if (this.source === "popular_item") {
        this.$snowplow.trackRemovePopularItem(itemID, quantity, rank)
      } else if (this.source === "search_item") {
        this.$snowplow.trackRemoveSearchItem(itemID, quantity, rank)
      }
    },

    getItemClone(storeItem): Item {
      const item: Item = Object.assign({}, storeItem)
      if (item.fake_id) item.quantity = this.getItemQuantity(item.fake_id)
      return item
    }
  }
})
</script>

<style lang="css" module>
.item {
  @apply flex flex-col justify-between py-ds-xxs;

  &:global(.zero) {
    @apply cursor-pointer;
  }

  p,
  div {
    @apply text-base leading-normal select-none;
  }

  p:first-child {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;

    @supports (-webkit-line-clamp: 2) {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: initial;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }
  }

  small {
    @apply text-sm text-gray-550 font-normal select-none leading-base;
  }

  .itemControls {
    @apply min-h-full flex gap-ds-xxs items-center text-center;

    span {
      @apply block w-ds-ml;
    }

    button {
      @apply h-ds-ml w-[80px] text-sm text-white bg-blue-primary rounded-[100px];
    }
  }
}

@screen lg {
  .item {
    & > div > div {
      @apply min-h-ds-xl;
    }

    p,
    div {
      @apply leading-normal;
    }

    &:hover {
      @apply bg-gray-140;
    }

    .itemControls {
      span,
      button {
        @apply font-bold;
      }
    }
  }
}
</style>
